<template>
  <nav class="c-paginator">
    <ul class="c-paginator__list">
      <li class="c-paginator__item">
        <component
          :is="component"
          @click="onPaginatorItemClicked(1, isFirst)"
          class="c-paginator__arrow"
          :disabled="isFirst"
        >
          <i class="icon-chevron-first"></i>
        </component>
      </li>

      <li class="c-paginator__item">
        <component
          :is="component"
          @click="onPaginatorItemClicked(currentPage - 1, isFirst)"
          class="c-paginator__arrow"
          :disabled="isFirst"
        >
          <i class="icon-chevron-prev"></i>
        </component>
      </li>

      <li
        class="c-paginator__item c-paginator__item--number"
        v-for="numberButton in numberButtonsToShow"
        :key="numberButton.number"
      >
        <component
          :is="component"
          @click="onPaginatorItemClicked(numberButton.number)"
          :class="[
            'c-paginator__number',
            { isCurrent: numberButton.isCurrent },
          ]"
        >
          {{ numberButton.number }}
        </component>
      </li>

      <li class="c-paginator__item">
        <component
          :is="component"
          @click="onPaginatorItemClicked(currentPage + 1, isLast)"
          class="c-paginator__arrow"
          :disabled="isLast"
        >
          <i class="icon-chevron-next"></i>
        </component>
      </li>

      <li class="c-paginator__item">
        <component
          :is="component"
          @click="onPaginatorItemClicked(pageAmount, isLast)"
          class="c-paginator__arrow"
          :disabled="isLast"
        >
          <i class="icon-chevron-last"></i>
        </component>
      </li>
    </ul>
  </nav>
</template>

<script>
import { BREAKPOINT_XS } from '~/js/constants/breakpoints'
import { getUrlWithQueryParams, getParam } from '~/js/helpers/url-helper'

export default {
  name: 'ComponentPaginator',
  props: {
    buttonsToShowDesktop: { type: Number, default: 10 },
    buttonsToShowMobile: { type: Number, default: 3 },
    hasNextPage: { type: Boolean, default: false },
    itemPerPage: { type: Number, default: 10 },
    itemTotal: { type: Number, required: true, default: 1 },
    queryKey: { type: String, default: 'page' },
    useLink: { type: Boolean, default: true },
    currentPageFromParent: { type: Number },
  },
  data() {
    return {
      currentPage: this.currentPageFromParent || 1,
    }
  },
  computed: {
    component() {
      return this.useLink ? 'a' : 'button'
    },
    isFirst() {
      return this.currentPage === 1 || undefined
    },
    isLast() {
      return (
        (!this.hasNextPage && this.currentPage === this.pageAmount) ||
        undefined
      )
    },
    numberButtonsToShow() {
      const buttonsToShow =
        window.innerWidth > BREAKPOINT_XS
          ? this.buttonsToShowDesktop
          : this.buttonsToShowMobile

      const offset = Math.floor((buttonsToShow - 1) / 2)
      let start = Math.max(1, this.currentPage - offset)
      let end = start + buttonsToShow - 1
      if (end > this.pageAmount) {
        end = this.pageAmount
        start = Math.max(this.pageAmount - buttonsToShow + 1, 1)
      }
      return Array(end - start + 1)
        .fill({})
        .map((_, index) => {
          const number = start + index
          const isCurrent = number === this.currentPage
          const attributes = this.useLink
            ? {
                href: isCurrent
                  ? undefined
                  : getUrlWithQueryParams({ [this.queryKey]: number }),
              }
            : {
                type: 'button',
              }
          return { attributes, number, isCurrent }
        })
    },
    pageAmount() {
      const knownPageAmount = Math.ceil(this.itemTotal / this.itemPerPage)
      return Math.max(1, knownPageAmount + (this.hasNextPage ? 1 : 0))
    },
  },
  created() {
    this.currentPage =
      getParam(this.queryKey) === null ? 1 : Number(getParam(this.queryKey))
    window.addEventListener('popstate', () => {
      this.currentPage =
        getParam(this.queryKey) === null ? 1 : Number(getParam(this.queryKey))
    })
  },
  watch: {
    currentPageFromParent(newPage) {
      this.currentPage = newPage
    },
  },
  methods: {
    onPaginatorItemClicked(number, disabled) {
      if (disabled) return
      if (this.useLink) {
        window.location.href = getUrlWithQueryParams({
          [this.queryKey]: number,
        })
      } else {
        this.currentPage = number
        this.$emit('paginator-item-clicked', number)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '~/scss/generic/variables';
@use '~/fonts/icons/icons';

.c-paginator {
  &__list {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__item {
    display: inline-block;

    &--number {
      margin: 0 10px;
    }
  }

  &__number,
  &__arrow {
    color: variables.$color-accent;
    display: inline-block;
    line-height: 28px;
    text-align: center;
    width: 28px;
    height: 28px;

    &[disabled] {
      color: variables.$color-text-gray;
    }
  }

  &__number {
    border-radius: 17px;
    border: 1px solid variables.$color-accent;
    font-size: 14px;
    transition: 0.3s;

    &.isCurrent {
      cursor: default;
      opacity: 1;
    }

    &.isCurrent,
    &:hover {
      background-color: variables.$color-accent;
      color: #fff;
    }
  }

  &__arrow {
    font-size: 16px;
  }
}
</style>
